import React from "react";
import { Button } from "./ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * ErrorBoundary - Catches React errors to prevent white screens.
 * Production-ready fallback for Aurora Geriátrica SaaS.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Aurora Geriátrica ErrorBoundary:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    const isAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    window.location.href = isAdmin ? "/admin" : "/pacientes";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-[400px] flex flex-col items-center justify-center p-8 bg-slate-50"
          data-testid="error-boundary"
        >
          <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">
              Algo salió mal
            </h2>
            <p className="text-slate-600 text-sm mb-6">
              Ha ocurrido un error inesperado. Puede intentar de nuevo o volver al inicio.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={this.handleRetry}
                className="border-slate-200"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
              <Button
                onClick={this.handleGoHome}
                className="bg-slate-800 hover:bg-slate-700"
              >
                <Home className="h-4 w-4 mr-2" />
                {typeof window !== "undefined" && window.location.pathname.startsWith("/admin")
                  ? "Volver al panel"
                  : "Ir a Pacientes"}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
